<template>
  <div>
    <!-- Time Doctor Hours Summary -->
    <v-card elevation="0" class="mb-4">
      <v-card-title>
        <v-icon left color="deep-purple">mdi-clock-check-outline</v-icon>
        <span style="font-size: 1.3rem; font-weight: 600">
          Your Tracked Hours
        </span>
        <v-spacer></v-spacer>
        <template v-if="timeDoctorAuthorized">
          <v-chip color="deep-purple" outlined class="mr-2">
            <v-icon left small>mdi-sigma</v-icon>
            Total: {{ totalTimeDoctorHours }}
          </v-chip>
          <v-btn small outlined color="deep-purple" @click="fetchTimeDoctorData" :loading="isLoadingTimeDoctorData"
            class="text-none mr-2">
            <v-icon left small>mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </template>
      </v-card-title>
      <v-card-subtitle>
        <span style="font-size: 0.8rem; font-weight: 500">
          Time Doctor Classic + Time Doctor 2 + DGTasks hours for the selected payroll period (overlapping time counted once)
        </span>
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-card-text>
        <!-- Authorization Required - Admin must connect -->
        <div v-if="!timeDoctorAuthorized && !isCheckingAuth" class="text-center py-6">
          <v-icon size="64" color="grey lighten-1">mdi-link-variant-off</v-icon>
          <div class="mt-4 text-h6 grey--text">Time Doctor Not Connected</div>
          <div class="mt-2 text-body-2 grey--text mb-4">
            Please contact your administrator to connect Time Doctor integration
          </div>
        </div>

        <!-- Loading Auth Status -->
        <div v-else-if="isCheckingAuth" class="text-center py-6">
          <v-progress-circular indeterminate color="deep-purple"></v-progress-circular>
          <div class="mt-4">Checking authorization status...</div>
        </div>

        <!-- Authorized Content -->
        <template v-else>
          <!-- Date Range Selector -->
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <v-menu v-model="timeDoctorDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
                min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="formattedTimeDoctorDateRange" label="Payroll Period" outlined dense readonly
                    v-bind="attrs" v-on="on" prepend-inner-icon="mdi-calendar-range" clearable
                    @click:clear="clearTimeDoctorDateRange" hint="Select the date range to fetch your tracked hours"
                    persistent-hint></v-text-field>
                </template>
                <v-date-picker v-model="timeDoctorDateRange" range no-title scrollable
                  @change="onTimeDoctorDateChange"></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" md="6" class="d-flex align-center">
              <v-chip-group>
                <v-chip small outlined @click="setCurrentPayrollPeriod" color="deep-purple">
                  <v-icon left small>mdi-calendar-today</v-icon>
                  Current Period
                </v-chip>
                <v-chip small outlined @click="setPreviousPayrollPeriod">
                  <v-icon left small>mdi-calendar-arrow-left</v-icon>
                  Previous Period
                </v-chip>
              </v-chip-group>
            </v-col>
          </v-row>

          <v-progress-linear v-if="isLoadingTimeDoctorData" indeterminate color="deep-purple"></v-progress-linear>
          
          <!-- Error message -->
          <v-alert v-else-if="timeDoctorError" type="error" dense outlined class="mb-4">
            {{ timeDoctorError }}
            <template v-if="timeDoctorError.includes('authorization')">
              <span class="text-caption ml-2">(Contact admin to re-authorize)</span>
            </template>
          </v-alert>
          
          <!-- No data message -->
          <div v-else-if="!timeDoctorData || !timeDoctorData.items || timeDoctorData.items.length === 0"
            class="text-center py-4 grey--text">
            <v-icon large color="grey lighten-1">mdi-clock-outline</v-icon>
            <div class="mt-2">No tracked hours found for the selected period</div>
            <div class="text-caption">Select a date range and click Refresh to fetch data</div>
          </div>
          
          <!-- Time Doctor Data Table -->
          <div v-else>
            <!-- Summary -->
            <v-alert type="info" dense outlined class="mb-4">
              <v-row no-gutters align="center">
                <v-col>
                  <strong>Period:</strong> {{ formatDate(timeDoctorData.period?.start) }} - {{
                    formatDate(timeDoctorData.period?.end) }}
                </v-col>
                <v-col class="text-right">
                  <strong>Total Time:</strong>
                  <v-chip small color="deep-purple" text-color="white" class="ml-2">
                    {{ timeDoctorData.total_formatted || '0h 00m' }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-alert>

            <!-- Worklogs Table -->
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Project</th>
                    <th class="text-left">Task</th>
                    <th class="text-left">User</th>
                    <th class="text-right">Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(entry, i) in timeDoctorData.items" :key="i">
                    <td>
                      <v-chip x-small outlined color="deep-purple">{{ entry.project_name || 'Unknown' }}</v-chip>
                      <v-chip v-if="entry.source" x-small label class="ml-1" :color="sourceColor(entry.source)">
                        {{ sourceLabel(entry.source) }}
                      </v-chip>
                    </td>
                    <td>{{ entry.task_name || 'Unknown Task' }}</td>
                    <td>{{ entry.user_name || '-' }}</td>
                    <td class="text-right">
                      <strong>{{ entry.formatted_time || `${entry.hours}h ${entry.minutes}m` }}</strong>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="grey lighten-4">
                    <td colspan="3" class="text-right font-weight-bold">Total:</td>
                    <td class="text-right font-weight-bold">
                      {{ timeDoctorData.total_formatted || '0h 00m' }}
                    </td>
                  </tr>
                </tfoot>
              </template>
            </v-simple-table>

            <div class="text-caption grey--text mt-2">
              Showing {{ timeDoctorData.items_count || 0 }} worklog entries
            </div>
          </div>
        </template>
      </v-card-text>
    </v-card>

    <v-card elevation="2">
      <v-card-title class="py-3" style="background: linear-gradient(135deg, #673ab7 0%, #512da8 100%);">
        <v-icon left color="white">mdi-briefcase-account</v-icon>
        <span style="color: white; font-weight: 600;">PT Payroll</span>
      </v-card-title>

      <v-tabs v-model="activeTab" background-color="grey lighten-4" slider-color="deep-purple">
        <v-tab class="text-none">
          <v-icon left small>mdi-file-document-edit</v-icon>
          PT Payroll Submission
        </v-tab>
        <v-tab class="text-none">
          <v-icon left small>mdi-history</v-icon>
          PT Submissions History
          <v-chip v-if="myPTSubmissions && myPTSubmissions.length" x-small class="ml-2" color="deep-purple" text-color="white">
            {{ myPTSubmissions.length }}
          </v-chip>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Tab 1: PT Payroll Submission Form -->
        <v-tab-item>
          <!-- PT Success Confirmation -->
          <v-card-text v-if="ptSubmissionSuccess" class="text-center py-8">
            <v-icon size="80" color="success">mdi-check-circle</v-icon>
            <div class="text-h5 mt-4 success--text">PT Payroll Submitted Successfully!</div>
            <div class="text-body-1 grey--text mt-2 mb-6">
              Your PT payroll submission has been received and is pending review.
            </div>
            <v-btn color="primary" @click="startNewPTSubmission" class="text-none">
              <v-icon left small>mdi-plus</v-icon>
              Submit Another PT Payroll
            </v-btn>
          </v-card-text>
          
          <!-- PT Submission Form -->
          <v-card-text v-else>
            <v-alert type="info" outlined dense class="mb-4">
              <strong>Profitable Tradesmen (PT) Payroll Submission</strong>
              <div class="text-caption mt-1">This form is specifically for PT contractors.</div>
            </v-alert>
            
            <v-form ref="ptPayrollForm" v-model="ptFormValid" lazy-validation>
              <v-row>
                <!-- Email (prefilled) -->
                <v-col cols="12" md="6">
                  <v-text-field v-model="ptFormData.email" label="Email *" outlined dense readonly
                    :rules="[rules.required, rules.email]" prepend-inner-icon="mdi-email"></v-text-field>
                </v-col>

                <!-- Name (prefilled) -->
                <v-col cols="12" md="6">
                  <v-text-field v-model="ptFormData.name" label="Name *" outlined dense readonly :rules="[rules.required]"
                    prepend-inner-icon="mdi-account"></v-text-field>
                </v-col>

                <!-- PT-Specific: Name of Client -->
                <v-col cols="12" md="6">
                  <v-text-field v-model="ptFormData.clientName" label="Name of Client *" outlined dense
                    :rules="[rules.required]" prepend-inner-icon="mdi-domain"
                    placeholder="e.g., ABC Company"></v-text-field>
                </v-col>

                <!-- Invoice Service Period -->
                <v-col cols="12" md="6">
                  <v-menu v-model="ptDateRangeMenu" :close-on-content-click="false" transition="scale-transition" offset-y
                    min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="ptFormattedDateRange" label="Invoice Service Period *" outlined dense readonly
                        v-bind="attrs" v-on="on" :rules="[rules.required]" prepend-inner-icon="mdi-calendar-range" clearable
                        @click:clear="clearPTDateRange"></v-text-field>
                    </template>
                    <v-date-picker v-model="ptFormData.invoicePeriod" range no-title scrollable
                      @change="ptDateRangeMenu = false"></v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Invoice File (PDF) -->
                <v-col cols="12" md="6">
                  <v-file-input v-model="ptFormData.invoiceFile" label="Invoice (PDF) *" outlined dense accept=".pdf"
                    :rules="[rules.required, rules.pdfFile]" prepend-icon="" prepend-inner-icon="mdi-file-pdf-box"
                    show-size></v-file-input>
                </v-col>

                <!-- Invoice Number -->
                <v-col cols="12" md="6">
                  <v-text-field v-model="ptFormData.invoiceNumber" label="Invoice Number *" outlined dense
                    :rules="[rules.required]" prepend-inner-icon="mdi-file-document"
                    placeholder="e.g., PT-INV-2024-001"></v-text-field>
                </v-col>

                <!-- Hours Worked -->
                <v-col cols="12" md="6">
                  <v-row no-gutters>
                    <v-col cols="6" class="pr-2">
                      <v-text-field v-model.number="ptFormData.hoursWorked.hours" label="Hours *" outlined dense type="number"
                        min="0" max="999" :rules="[rules.required, rules.positiveNumber]"
                        prepend-inner-icon="mdi-clock-outline"></v-text-field>
                    </v-col>
                    <v-col cols="6" class="pl-2">
                      <v-text-field v-model.number="ptFormData.hoursWorked.minutes" label="Minutes *" outlined dense
                        type="number" min="0" max="59" :rules="[rules.required, rules.minutes]"></v-text-field>
                    </v-col>
                  </v-row>
                </v-col>

                <!-- Currency -->
                <v-col cols="12" md="3">
                  <v-select v-model="ptFormData.currency" :items="['USD', 'PHP']" label="Currency *" outlined dense
                    :rules="[rules.required]" prepend-inner-icon="mdi-cash"></v-select>
                </v-col>

                <!-- Invoice Amount -->
                <v-col cols="12" md="3">
                  <v-text-field v-model.number="ptFormData.invoiceAmount" label="Invoice Amount *" outlined dense
                    type="number" min="0" step="0.01" :rules="[rules.required, rules.positiveNumber]"
                    :prepend-inner-icon="ptFormData.currency === 'PHP' ? 'mdi-currency-php' : 'mdi-currency-usd'"
                    :prefix="ptFormData.currency === 'PHP' ? '₱' : '$'"
                    :hint="calculatedAmountFormatted ? `Calculated: ${calculatedAmountFormatted}` : null"
                    :persistent-hint="!!calculatedAmountFormatted"></v-text-field>
                  <!-- Amount Mismatch Warning -->
                  <v-alert v-if="amountMismatch" type="warning" dense text class="mt-1 mb-0">
                    <div class="d-flex align-center">
                      <v-icon small color="warning" class="mr-2">mdi-alert</v-icon>
                      <span class="text-caption">
                        Invoice amount differs from calculated ({{ calculatedAmountFormatted }}) by 
                        <strong>{{ amountVariancePercent > 0 ? '+' : '' }}{{ amountVariancePercent.toFixed(1) }}%</strong>
                      </span>
                    </div>
                  </v-alert>
                  <!-- No Hourly Rate Warning -->
                  <v-alert v-else-if="!user?.hourly_rate" type="info" dense text class="mt-1 mb-0">
                    <span class="text-caption">
                      <v-icon small color="info" class="mr-1">mdi-information</v-icon>
                      No hourly rate configured. Amount calculation unavailable.
                    </span>
                  </v-alert>
                </v-col>

                <!-- PT-Specific: Capacity Utilization Form -->
                <v-col cols="12" md="6">
                  <v-file-input v-model="ptFormData.capacityUtilizationFile" label="Capacity Utilization / Completion Notice Form" outlined dense
                    accept=".pdf,.jpg,.jpeg,.png" prepend-icon="" prepend-inner-icon="mdi-file-document-check"
                    show-size></v-file-input>
                  
                  <v-alert type="info" dense text class="mt-2 mb-0">
                    <div class="text-caption">
                      <div class="mb-2">
                        To help us work more effectively together, we are introducing a new process designed to support you, provide clarity, and recognize your contributions.
                      </div>
                      <div class="font-weight-bold">
                        PLEASE FILL OUT AND UPLOAD THE COMPLETION NOTICE:
                        <a href="https://docs.google.com/forms/d/e/1FAIpQLSe3ioXygDPekW-jQXK5VE0ov5oVdO-TBqav0egh7H2T4gNtBQ/viewform" target="_blank" class="text-decoration-underline primary--text">
                          Completion Notice Form
                        </a>
                      </div>
                    </div>
                  </v-alert>
                </v-col>

                <!-- PT-Specific: Approval of Overtime -->
                <v-col cols="12" md="6">
                  <v-file-input v-model="ptFormData.overtimeApprovalFile" label="Approval of Overtime" outlined dense
                    accept=".pdf,.jpg,.jpeg,.png" prepend-icon="" prepend-inner-icon="mdi-clock-check"
                    show-size></v-file-input>
                  
                  <v-alert type="info" dense text class="mt-2 mb-0">
                    <div class="text-caption">
                      <div class="mb-1 font-weight-bold">Upload Screenshot of Approval</div>
                      <div class="mb-1">Guidelines:</div>
                      <ul class="pl-4 mb-0">
                        <li>Date form is filled out - e.g., 29 December 2022</li>
                        <li>Overtime hours from - e.g., 05-16 December 2022</li>
                      </ul>
                    </div>
                  </v-alert>
                </v-col>

                <!-- PT-Specific: Employee Database Form (One-time) -->
                <v-col cols="12" md="6">
                  <v-file-input v-model="ptFormData.employeeDatabaseFormFile" 
                    :label="ptFormStatus.hasEmployeeDatabaseForm ? 'Employee Database Form (Already on file)' : 'Employee Database Form'" 
                    outlined dense
                    accept=".pdf,.jpg,.jpeg,.png" prepend-icon="" prepend-inner-icon="mdi-database-check"
                    show-size></v-file-input>
                  
                  <v-alert v-if="ptFormStatus.hasEmployeeDatabaseForm" type="success" dense text class="mt-2 mb-2">
                    <div class="d-flex align-center">
                      <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                      <span class="text-caption">Employee Database Form already on file from a previous submission. Upload only if you want to update it.</span>
                    </div>
                  </v-alert>
                  
                  <v-alert type="info" dense text class="mt-2 mb-0">
                    <div class="text-caption">
                      <div class="mb-1 font-weight-bold">Kindly attach a screenshot of the duly filled employee database form (FILL OUT ONCE)</div>
                      <div>
                        EMPLOYEE DATABASE:
                        <a href="https://docs.google.com/forms/d/e/1FAIpQLSeEZA0Ok05lzb_JBOxeRFBhLmEwmgu6EsTp4LuaFlu476Cqxw/viewform" target="_blank" class="text-decoration-underline primary--text">
                          Employee Database Form
                        </a>
                      </div>
                    </div>
                  </v-alert>
                </v-col>

                <!-- PT-Specific: Signature Acknowledgement (One-time) -->
                <v-col cols="12" md="6">
                  <v-file-input v-model="ptFormData.signatureAcknowledgementFile" 
                    :label="ptFormStatus.hasSignatureAcknowledgement ? 'Signature/Acknowledgement (Already on file)' : 'Signature/Acknowledgement *'" 
                    outlined dense
                    :rules="[rules.required]"
                    accept=".pdf,.jpg,.jpeg,.png" prepend-icon="" prepend-inner-icon="mdi-signature-freehand"
                    show-size></v-file-input>
                  
                  <v-alert v-if="ptFormStatus.hasSignatureAcknowledgement" type="success" dense text class="mt-2 mb-2">
                    <div class="d-flex align-center">
                      <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                      <span class="text-caption">Signature acknowledgement already on file. Upload only if you want to update it.</span>
                    </div>
                  </v-alert>
                  
                  <v-alert type="warning" dense text class="mt-2 mb-0">
                    <div class="text-caption">
                      <div class="mb-2 font-weight-bold">Please read and acknowledge:</div>
                      <div class="mb-2" style="font-style: italic;">
                        I hereby acknowledge that I am working SOLELY for Outsourceforce.ai.
                      </div>
                      <div class="mb-2" style="font-style: italic;">
                        I will not at any time or in any manner, either directly or indirectly, use for my own personal benefit, or divulge, disclose or communicate in any manner any information that is proprietary to Outsourceforce.ai and its members.
                      </div>
                      <div class="mb-2" style="font-style: italic;">
                        My contract is with Outsourceforce.ai. I understand I cannot work for clients directly within 2 years after ending my contract. I do understand I have to pay €5000 and Outsourceforce.ai has the rights to take legal action if I breach this.
                      </div>
                      <div class="font-weight-bold error--text">
                        Please upload a copy of your signature above printed name with date.
                      </div>
                    </div>
                  </v-alert>
                </v-col>

                <v-col cols="12" md="6">
                  <v-file-input v-model="ptFormData.receiptFile" label="Acknowledgement Receipt *" outlined dense
                    :rules="[rules.required]" prepend-icon="" prepend-inner-icon="mdi-receipt" show-size></v-file-input>
                  
                  <v-alert type="info" dense text class="mt-2 mb-0">
                    <div class="text-caption">
                      <div class="mb-2"><strong>NOTE:</strong> This Acknowledgement Receipt is being submitted as part of the requirements and will only take effect once payment has been received and credited for the specified invoice number and client indicated in this Google Form.</div>
                      <div>
                        <a href="https://docs.google.com/document/d/1XMhd7V8-2EDabzGbrsZHteaNGLK1qX4n/edit" target="_blank" class="text-decoration-underline primary--text">
                          AR TEMPLATE
                        </a>
                      </div>
                    </div>
                  </v-alert>
                </v-col>

                <!-- Certificate of Registration -->
                <v-col cols="12" md="6">
                  <v-file-input v-model="ptFormData.corFile" :label="ptFormStatus.hasCor ? 'Copy of Certificate of Registration (Already on file)' : 'Copy of Certificate of Registration *'" outlined dense
                    :rules="ptFormStatus.hasCor ? [] : [rules.required]" prepend-icon="" prepend-inner-icon="mdi-certificate" show-size></v-file-input>
                  
                  <v-alert v-if="ptFormStatus.hasCor" type="success" dense text class="mt-2 mb-2">
                    <div class="d-flex align-center">
                      <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                      <span class="text-caption">COR already on file from a previous submission. Upload only if you want to update it.</span>
                    </div>
                  </v-alert>
                  
                  <v-alert type="info" dense text class="mt-2 mb-0">
                    <div class="text-caption">
                      <div class="mb-1">
                        If you are not a registered freelancer / self employed person we recommend you start the process. It's a very easy straight forward process.
                      </div>
                      <div class="mb-1">
                        A guide can be found here with instructions:
                        <a href="https://docs.google.com/document/d/1FpUN1E3bdWFxTbqrudFnxZC_togtFnx50pQ0z7zQS3M/edit?tab=t.0" target="_blank" class="text-decoration-underline">
                          Registration Guide
                        </a>
                      </div>
                      <div class="font-weight-bold">
                        Deadline: All team members are required to submit their COR within two (2) months of their date of employment.
                      </div>
                    </div>
                  </v-alert>
                </v-col>

                <!-- Tax Return Files (Multiple) -->
                <v-col cols="12">
                  <v-file-input 
                    v-model="ptFormData.taxReturnFiles" 
                    :label="ptFormStatus.hasTaxReturns ? 'Tax Return Files (Uploaded for this quarter)' : 'Tax Return Files *'" 
                    outlined 
                    dense
                    multiple 
                    accept=".pdf,.jpg,.jpeg,.png" 
                    prepend-icon="" 
                    prepend-inner-icon="mdi-file-document-multiple"
                    :rules="ptFormStatus.hasTaxReturns ? [] : [rules.required]"
                    show-size 
                    counter>
                  </v-file-input>
                  
                  <v-alert :type="ptTaxReturnStatus.color" dense text class="mt-2 mb-2">
                    <div class="d-flex align-center">
                      <v-icon small :color="ptTaxReturnStatus.color" class="mr-2">
                        {{ ptTaxReturnStatus.status === 'uploaded' ? 'mdi-check-circle' : ptTaxReturnStatus.status === 'overdue' ? 'mdi-alert-circle' : 'mdi-clock-outline' }}
                      </v-icon>
                      <span class="text-caption">{{ ptTaxReturnStatus.message }}</span>
                    </div>
                  </v-alert>
                  
                  <v-alert type="info" dense text class="mt-2 mb-0">
                    <div class="text-caption">
                      <div class="mb-2">
                        <strong>Philippine VAs:</strong> Submit scanned copies of your Tax Returns for the 2025 tax year. These returns must correspond to the tax types listed in your COR (Certificate of Registration):
                      </div>
                      <ul class="pl-6 mb-2">
                        <li>Forms 2551Q, 1701Q, 1701A, or 1701</li>
                        <li>PDF copy of the BIR's email confirmation</li>
                        <li>Proof of payment / Tax receipts</li>
                      </ul>
                      <div class="mt-2">
                        <strong>South African VAs:</strong> Submit a scanned copy of your 2025 ITA34 (Notice of Assessment).
                      </div>
                    </div>
                  </v-alert>
                </v-col>

                <v-col cols="12">
                  <v-textarea v-model="ptFormData.comments" label="Comments (Optional)" outlined dense rows="3"
                    prepend-inner-icon="mdi-comment-text-outline"
                    placeholder="Any additional notes or comments..."></v-textarea>
                </v-col>
              </v-row>
            </v-form>

            <v-divider class="my-4"></v-divider>
            
            <div class="d-flex justify-end">
              <v-btn text @click="resetPTForm" class="text-none mr-2">
                <v-icon left small>mdi-refresh</v-icon>
                Reset
              </v-btn>
              <v-btn color="deep-purple" @click="submitPTForm" :disabled="!ptFormValid || isSubmitting" :loading="isSubmitting" class="text-none white--text">
                <v-icon left small>mdi-send</v-icon>
                Submit PT Payroll
              </v-btn>
            </div>
          </v-card-text>
        </v-tab-item>

        <!-- Tab 2: PT Submissions History -->
        <v-tab-item>
          <v-card-text>
            <div v-if="!myPTSubmissions || myPTSubmissions.length === 0" class="text-center py-8 grey--text">
              <v-icon size="64" color="grey lighten-1">mdi-inbox-outline</v-icon>
              <div class="mt-4 text-h6">No PT submissions yet</div>
              <div class="text-body-2">Your PT payroll submissions will appear here</div>
            </div>
            <v-row v-else class="submissions-wrapper">
              <v-col v-for="submission in myPTSubmissions" :key="submission.id" cols="12" md="6" lg="4">
                <v-card outlined class="submission-card">
                  <v-card-title class="pb-1">
                    <v-avatar size="36" color="deep-purple" class="mr-2">
                      <span class="white--text text-body-2">
                        {{ getInitials(ptFormData.name) }}
                      </span>
                    </v-avatar>
                    <div>
                      <div style="font-size: 0.95rem; font-weight: 600">
                        {{ ptFormData.name }}
                      </div>
                      <div style="font-size: 0.75rem; color: #666; font-weight: 400;">
                        Invoice No: {{ submission.invoice_number || 'N/A' }}
                      </div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-chip small :color="getSubmissionStatusColor(submission.status)" text-color="white" label>
                      {{ submission.status }}
                    </v-chip>
                  </v-card-title>
                  <v-card-subtitle class="pt-1">
                    <v-icon x-small>mdi-account-tie</v-icon>
                    Client: {{ submission.client_name || 'N/A' }}
                    <span class="mx-2">|</span>
                    <v-icon x-small>mdi-domain</v-icon>
                    {{ submission.company?.name || 'N/A' }}
                  </v-card-subtitle>
                  <v-divider></v-divider>
                  <v-card-text class="py-2">
                    <v-row dense>
                      <v-col cols="6">
                        <div class="detail-label">Period</div>
                        <div class="detail-value">
                          {{ formatDate(submission.invoice_period_from) }} - {{ formatDate(submission.invoice_period_to) }}
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="detail-label">Hours Worked</div>
                        <div class="detail-value">
                          {{ submission.hours_worked || '0' }}h
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="detail-label">Invoice Amount</div>
                        <div class="detail-value">
                          {{ submission.currency || 'USD' }} {{ formatCurrency(submission.invoice_amount) }}
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="detail-label">Submitted</div>
                        <div class="detail-value">
                          {{ formatDateTime(submission.submitted_at) }}
                        </div>
                      </v-col>
                    </v-row>
                    <div v-if="submission.comments" class="mt-2">
                      <div class="detail-label">Comments</div>
                      <div class="detail-value" style="font-style: italic">
                        "{{ submission.comments }}"
                      </div>
                    </div>
                    <div v-if="submission.status === 'denied' && submission.deny_reason" class="mt-2">
                      <div class="detail-label" style="color: #f44336">
                        Deny Reason
                      </div>
                      <div class="detail-value" style="color: #f44336">
                        {{ submission.deny_reason }}
                      </div>
                    </div>
                    <div v-if="submission.status === 'approved' && submission.approved_amount" class="mt-2">
                      <div class="detail-label" style="color: #4caf50">
                        Approved Amount
                      </div>
                      <div class="detail-value" style="color: #4caf50">
                        USD {{ formatCurrency(submission.approved_amount) }}
                      </div>
                    </div>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-chip v-if="submission.status === 'pending'" small color="warning" outlined>
                      <v-icon left small>mdi-clock-outline</v-icon>
                      Pending Review
                    </v-chip>
                    <v-chip v-else-if="submission.status === 'approved'" small color="success" outlined>
                      <v-icon left small>mdi-check-circle</v-icon>
                      Approved
                    </v-chip>
                    <v-chip v-else-if="submission.status === 'denied'" small color="error" outlined>
                      <v-icon left small>mdi-close-circle</v-icon>
                      Denied
                    </v-chip>
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="viewPTSubmissionDetails(submission)">
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- View Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="700px">
      <v-card v-if="selectedSubmission">
        <v-card-title class="deep-purple white--text">
          <v-icon left color="white">mdi-file-document</v-icon>
          PT Submission Details
          <v-spacer></v-spacer>
          <v-btn icon dark @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-simple-table>
            <tbody>
              <tr>
                <td style="font-weight: 600; width: 40%">Employee</td>
                <td>{{ ptFormData.name }}</td>
              </tr>
              <tr>
                <td style="font-weight: 600">Email</td>
                <td>{{ ptFormData.email }}</td>
              </tr>
              <tr>
                <td style="font-weight: 600">Client Name</td>
                <td>{{ selectedSubmission.client_name || 'N/A' }}</td>
              </tr>
              <tr>
                <td style="font-weight: 600">Invoice Number</td>
                <td>{{ selectedSubmission.invoice_number }}</td>
              </tr>
              <tr>
                <td style="font-weight: 600">Company</td>
                <td>{{ selectedSubmission.company?.name || 'N/A' }}</td>
              </tr>
              <tr>
                <td style="font-weight: 600">Service Period</td>
                <td>
                  {{ formatDate(selectedSubmission.invoice_period_from) }} - {{ formatDate(selectedSubmission.invoice_period_to) }}
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Hours Worked</td>
                <td>
                  {{ selectedSubmission.hours_worked || '0' }}h
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Invoice Amount</td>
                <td>
                  {{ selectedSubmission.currency || 'USD' }} {{ formatCurrency(selectedSubmission.invoice_amount) }}
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Status</td>
                <td>
                  <v-chip small :color="getSubmissionStatusColor(selectedSubmission.status)" text-color="white" label>
                    {{ selectedSubmission.status }}
                  </v-chip>
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Submitted</td>
                <td>{{ formatDateTime(selectedSubmission.submitted_at) }}</td>
              </tr>
              <tr v-if="selectedSubmission.approved_amount">
                <td style="font-weight: 600">Approved Amount</td>
                <td>
                  <span style="color: #4caf50; font-weight: 600;">
                    USD {{ formatCurrency(selectedSubmission.approved_amount) }}
                  </span>
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Invoice File</td>
                <td>
                  <v-chip small outlined v-if="selectedSubmission.invoice_file_path">
                    <v-icon left x-small>mdi-file-pdf-box</v-icon>
                    <a :href="`https://api.dgventureslimited.com/storage/${selectedSubmission.invoice_file_path}`" target="_blank" style="text-decoration: none; color: inherit;">
                      {{ selectedSubmission.invoice_file_path.split('/').pop() }}
                    </a>
                  </v-chip>
                  <span v-else class="grey--text">Not uploaded</span>
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Capacity Utilization Form</td>
                <td>
                  <v-chip small outlined v-if="selectedSubmission.capacity_utilization_form_path">
                    <v-icon left x-small>mdi-file-document-check</v-icon>
                    <a :href="`https://api.dgventureslimited.com/storage/${selectedSubmission.capacity_utilization_form_path}`" target="_blank" style="text-decoration: none; color: inherit;">
                      {{ selectedSubmission.capacity_utilization_form_path.split('/').pop() }}
                    </a>
                  </v-chip>
                  <span v-else class="grey--text">Not uploaded</span>
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Overtime Approval</td>
                <td>
                  <v-chip small outlined v-if="selectedSubmission.overtime_approval_path">
                    <v-icon left x-small>mdi-clock-check</v-icon>
                    <a :href="`https://api.dgventureslimited.com/storage/${selectedSubmission.overtime_approval_path}`" target="_blank" style="text-decoration: none; color: inherit;">
                      {{ selectedSubmission.overtime_approval_path.split('/').pop() }}
                    </a>
                  </v-chip>
                  <span v-else class="grey--text">Not uploaded</span>
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Employee Database Form</td>
                <td>
                  <v-chip small outlined v-if="selectedSubmission.employee_database_form_path">
                    <v-icon left x-small>mdi-database-check</v-icon>
                    <a :href="`https://api.dgventureslimited.com/storage/${selectedSubmission.employee_database_form_path}`" target="_blank" style="text-decoration: none; color: inherit;">
                      {{ selectedSubmission.employee_database_form_path.split('/').pop() }}
                    </a>
                  </v-chip>
                  <span v-else class="grey--text">Not uploaded</span>
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Signature/Acknowledgement</td>
                <td>
                  <v-chip small outlined v-if="selectedSubmission.signature_acknowledgement_path">
                    <v-icon left x-small>mdi-signature-freehand</v-icon>
                    <a :href="`https://api.dgventureslimited.com/storage/${selectedSubmission.signature_acknowledgement_path}`" target="_blank" style="text-decoration: none; color: inherit;">
                      {{ selectedSubmission.signature_acknowledgement_path.split('/').pop() }}
                    </a>
                  </v-chip>
                  <span v-else class="grey--text">Not uploaded</span>
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Acknowledgement Receipt</td>
                <td>
                  <v-chip small outlined v-if="selectedSubmission.acknowledgement_receipt_path">
                    <v-icon left x-small>mdi-receipt</v-icon>
                    <a :href="`https://api.dgventureslimited.com/storage/${selectedSubmission.acknowledgement_receipt_path}`" target="_blank" style="text-decoration: none; color: inherit;">
                      {{ selectedSubmission.acknowledgement_receipt_path.split('/').pop() }}
                    </a>
                  </v-chip>
                  <span v-else class="grey--text">Not uploaded</span>
                </td>
              </tr>
              <tr v-if="selectedSubmission.comments">
                <td style="font-weight: 600">Comments</td>
                <td>{{ selectedSubmission.comments }}</td>
              </tr>
              <tr v-if="selectedSubmission.deny_reason">
                <td style="font-weight: 600; color: #f44336">Deny Reason</td>
                <td style="color: #f44336">{{ selectedSubmission.deny_reason }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "EmployeePTPayrollView",
  props: {
    user: {
      type: Object,
      required: true,
    },
    companies: {
      type: Array,
      default: () => [],
    },
    myPTSubmissions: {
      type: Array,
      default: () => [],
    },
    ptFormStatus: {
      type: Object,
      default: () => ({
        hasEmployeeDatabaseForm: false,
        hasSignatureAcknowledgement: false,
        hasCor: false,
        hasTaxReturns: false,
      }),
    },
  },
  data() {
    return {
      activeTab: 0,
      // Time Doctor authorization
      timeDoctorAuthorized: false,
      isCheckingAuth: false,
      // Time Doctor user
      timeDoctorUserId: null,
      timeDoctorCompanyId: 41090,
      isLoadingTimeDoctorUser: false,
      // Time Doctor data
      timeDoctorDateRange: [],
      timeDoctorDateMenu: false,
      timeDoctorData: null,
      timeDoctorError: null,
      isLoadingTimeDoctorData: false,
      // PT Form Data
      ptFormValid: false,
      isSubmitting: false,
      ptSubmissionSuccess: false,
      ptDateRangeMenu: false,
      ptFormData: {
        email: "",
        name: "",
        clientName: "",
        invoicePeriod: [],
        company: null,
        invoiceNumber: "",
        currency: "USD",
        hoursWorked: {
          hours: null,
          minutes: null,
        },
        invoiceAmount: null,
        invoiceFile: null,
        capacityUtilizationFile: null,
        overtimeApprovalFile: null,
        employeeDatabaseFormFile: null,
        signatureAcknowledgementFile: null,
        receiptFile: null,
        corFile: null,
        taxReturnFiles: [],
        comments: "",
      },
      // Submission details dialog
      detailsDialog: false,
      selectedSubmission: null,
      // Validation rules
      rules: {
        required: (v) => !!v || v === 0 || "This field is required",
        email: (v) =>
          !v || /.+@.+\..+/.test(v) || "Please enter a valid email",
        positiveNumber: (v) =>
          v === null || v === "" || v >= 0 || "Must be a positive number",
        minutes: (v) =>
          v === null ||
          v === "" ||
          (v >= 0 && v <= 59) ||
          "Minutes must be between 0-59",
        pdfFile: (v) =>
          !v ||
          v.type === "application/pdf" ||
          "Please upload a PDF file",
      },
    };
  },
  computed: {
    ptFormattedDateRange() {
      if (
        !this.ptFormData.invoicePeriod ||
        this.ptFormData.invoicePeriod.length === 0
      ) {
        return "";
      }
      if (this.ptFormData.invoicePeriod.length === 1) {
        return this.formatDate(this.ptFormData.invoicePeriod[0]);
      }
      const sorted = [...this.ptFormData.invoicePeriod].sort();
      return `${this.formatDate(sorted[0])} - ${this.formatDate(sorted[1])}`;
    },
    formattedTimeDoctorDateRange() {
      if (!this.timeDoctorDateRange || this.timeDoctorDateRange.length === 0) {
        return "";
      }
      if (this.timeDoctorDateRange.length === 1) {
        return this.formatDate(this.timeDoctorDateRange[0]);
      }
      const sorted = [...this.timeDoctorDateRange].sort();
      return `${this.formatDate(sorted[0])} - ${this.formatDate(sorted[1])}`;
    },
    totalTimeDoctorHours() {
      if (this.timeDoctorData && this.timeDoctorData.total_formatted) {
        return this.timeDoctorData.total_formatted;
      }
      return "0h 0m";
    },
    timeDoctorHoursDecimal() {
      if (!this.timeDoctorData) return 0;
      return (this.timeDoctorData.total_hours || 0) + (this.timeDoctorData.total_minutes || 0) / 60;
    },
    /**
     * Get submitted hours as decimal value
     */
    submittedHoursDecimal() {
      const hours = this.ptFormData.hoursWorked.hours || 0;
      const minutes = this.ptFormData.hoursWorked.minutes || 0;
      return hours + minutes / 60;
    },
    /**
     * Calculate the expected amount based on hourly rate and submitted hours
     */
    calculatedAmount() {
      const hourlyRate = this.user?.hourly_rate;
      if (!hourlyRate || hourlyRate <= 0) return null;
      return this.submittedHoursDecimal * hourlyRate;
    },
    /**
     * Format the calculated amount with currency (USD for PT)
     */
    calculatedAmountFormatted() {
      if (this.calculatedAmount === null) return null;
      return `${this.ptFormData.currency || 'USD'} ${this.formatCurrency(this.calculatedAmount)}`;
    },
    /**
     * Check if invoice amount differs from calculated amount
     */
    amountMismatch() {
      if (this.calculatedAmount === null || !this.ptFormData.invoiceAmount) return false;
      // Allow 2% variance
      const variance = Math.abs(this.ptFormData.invoiceAmount - this.calculatedAmount) / this.calculatedAmount * 100;
      return variance > 2;
    },
    /**
     * Calculate amount variance percentage
     */
    amountVariancePercent() {
      if (this.calculatedAmount === null || this.calculatedAmount === 0) return 0;
      return ((this.ptFormData.invoiceAmount - this.calculatedAmount) / this.calculatedAmount) * 100;
    },
    currentQuarter() {
      const now = new Date();
      const month = now.getMonth();
      const quarter = Math.floor(month / 3) + 1;
      return { quarter, year: now.getFullYear() };
    },
    ptTaxReturnStatus() {
      const { quarter, year } = this.currentQuarter;
      const dueDates = {
        1: new Date(year, 4, 15),    // Q1: May 15
        2: new Date(year, 7, 15),    // Q2: Aug 15
        3: new Date(year, 10, 15),   // Q3: Nov 15
        4: new Date(year + 1, 3, 15), // Q4: Apr 15 next year
      };
      const dueDate = dueDates[quarter];
      const now = new Date();
      const isPastDue = now > dueDate;
      
      if (this.ptFormStatus.hasTaxReturns) {
        return { status: 'uploaded', message: `Tax returns uploaded for Q${quarter} ${year}`, color: 'success' };
      } else if (isPastDue) {
        return { status: 'overdue', message: `Tax returns overdue for Q${quarter} ${year}. Please upload as soon as possible.`, color: 'error' };
      } else {
        const formattedDue = this.$date ? this.$date(dueDate).format('MMM D, YYYY') : dueDate.toLocaleDateString();
        return { status: 'pending', message: `Tax returns due by ${formattedDue}. Please upload before the deadline.`, color: 'warning' };
      }
    },
  },
  watch: {
    user: {
      immediate: true,
      handler(newUser) {
        if (newUser) {
          this.ptFormData.email = newUser.email || "";
          this.ptFormData.name = this.getUserName(newUser);
        }
      },
    },
    timeDoctorData: {
      handler(newData) {
        if (newData && newData.total_formatted) {
          // Parse hours and minutes from Time Doctor data
          const parsed = this.parseTimeDoctorHours(newData.total_formatted);
          if (parsed) {
            this.ptFormData.hoursWorked.hours = parsed.hours;
            this.ptFormData.hoursWorked.minutes = parsed.minutes;
          }
          // Auto-calculate invoice amount if hourly rate exists
          if (this.user?.hourly_rate && this.calculatedAmount !== null) {
            this.ptFormData.invoiceAmount = Math.round(this.calculatedAmount * 100) / 100;
          }
        }
      },
    },
  },
  mounted() {
    // Check for OAuth callback code in URL
    this.handleOAuthCallback();
    // Check Time Doctor authorization status
    this.checkTimeDoctorAuth();
  },
  methods: {
    sourceLabel(source) {
      return { classic: 'Classic', td2: 'TD2', dgtasks: 'DGTasks' }[source] || source;
    },
    sourceColor(source) {
      return { td2: 'green lighten-4', dgtasks: 'blue lighten-4' }[source] || 'grey lighten-3';
    },
    // Time Doctor Methods
    async checkTimeDoctorAuth() {
      this.isCheckingAuth = true;
      try {
        const response = await this.$axios.get('/api/payroll/time-doctor/auth-status');
        console.log('Time Doctor auth status:', response.data);
        
        if (response.data.response && response.data.data) {
          // Hours include DGTasks, which needs no Time Doctor connection, so the panel always shows
          this.timeDoctorAuthorized = true;
          
          // If authorized, fetch user ID then set default payroll period
          if (this.timeDoctorAuthorized) {
            await this.fetchTimeDoctorUserId();
            this.setCurrentPayrollPeriod();
          }
        }
      } catch (error) {
        console.error('Error checking Time Doctor auth:', error);
        this.timeDoctorAuthorized = false;
      } finally {
        this.isCheckingAuth = false;
      }
    },
    async handleOAuthCallback() {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code) {
        console.log('OAuth callback detected, exchanging code for token...');
        try {
          const response = await this.$axios.get('/api/payroll/time-doctor/callback', {
            params: { code }
          });
          
          console.log('OAuth callback response:', response.data);
          
          if (response.data && response.data.response) {
            this.$toast.success('Time Doctor connected successfully!');
            this.timeDoctorAuthorized = true;
            
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
            
            // Fetch user ID then data now that we're authorized
            await this.fetchTimeDoctorUserId();
            this.setCurrentPayrollPeriod();
          } else {
            this.$toast.error(response.data?.message || 'Failed to connect Time Doctor');
          }
        } catch (error) {
          console.error('OAuth callback error:', error);
          this.$toast.error(error.response?.data?.message || 'Failed to connect Time Doctor');
        }
      }
    },
    calculatePayrollPeriod(date) {
      const today = this.$date(date);
      const anchorDate = this.$date('2026-01-12');
      const daysSinceAnchor = today.diff(anchorDate, 'day');
      const periodNumber = Math.floor(daysSinceAnchor / 14);
      const periodStart = anchorDate.add(periodNumber * 14, 'day');
      const periodEnd = periodStart.add(13, 'day');
      return {
        start: periodStart.format('YYYY-MM-DD'),
        end: periodEnd.format('YYYY-MM-DD'),
      };
    },
    setCurrentPayrollPeriod() {
      const today = this.$date();
      const period = this.calculatePayrollPeriod(new Date());
      // Sat/Sun already resolve to the period that's wrapping up that weekend
      // (periods run Mon-Sun), so only the Monday that starts a brand new
      // period needs special-casing: default to the period that just ended,
      // since employees typically submit on that first Monday for it.
      // A "middle" Monday one week into the current period should NOT trigger this.
      if (today.day() === 1 && today.format('YYYY-MM-DD') === period.start) {
        this.setPreviousPayrollPeriod();
        return;
      }
      this.timeDoctorDateRange = [period.start, period.end];
      this.ptFormData.invoicePeriod = [period.start, period.end];
      this.fetchTimeDoctorData();
    },
    setPreviousPayrollPeriod() {
      const currentPeriod = this.calculatePayrollPeriod(new Date());
      const previousStart = this.$date(currentPeriod.start).subtract(14, 'day');
      const previousEnd = previousStart.add(13, 'day');
      const periodDates = [
        previousStart.format('YYYY-MM-DD'),
        previousEnd.format('YYYY-MM-DD'),
      ];
      this.timeDoctorDateRange = periodDates;
      this.ptFormData.invoicePeriod = periodDates;
      this.fetchTimeDoctorData();
    },
    onTimeDoctorDateChange() {
      this.timeDoctorDateMenu = false;
      if (this.timeDoctorDateRange.length === 2) {
        this.fetchTimeDoctorData();
      }
    },
    clearTimeDoctorDateRange() {
      this.timeDoctorDateRange = [];
      this.timeDoctorData = null;
      this.timeDoctorError = null;
    },
    async fetchTimeDoctorUserId() {
      if (!this.user?.email) {
        console.warn('No user email available to fetch Time Doctor user ID');
        return;
      }
      if (this.user.time_doctor_user_id) {
        this.timeDoctorUserId = this.user.time_doctor_user_id;
        console.log('Using cached Time Doctor user ID:', this.timeDoctorUserId);
        return;
      }
      this.isLoadingTimeDoctorUser = true;
      this.timeDoctorError = null;
      try {
        console.log('Syncing Time Doctor user ID for email:', this.user.email);
        const response = await this.$axios.post('/api/payroll/time-doctor/sync-user-id');
        console.log('Time Doctor sync response:', response.data);
        if (response.data.response && response.data.data?.time_doctor_user_id) {
          this.timeDoctorUserId = response.data.data.time_doctor_user_id;
          console.log('Time Doctor user ID synced and cached:', this.timeDoctorUserId);
          this.$toast.success('Time Doctor user linked successfully');
        } else {
          console.warn('Time Doctor sync returned no user ID');
          this.timeDoctorError = 'Unable to sync Time Doctor user. Please try again.';
        }
      } catch (error) {
        console.error('Error syncing Time Doctor user:', error);
        const errorMessage = error.response?.data?.message || 'Please go to Settings in the upper right corner, click Edit Profile, and enter your Time Doctor email as your Secondary Email, then refresh this page.';
        this.timeDoctorError = errorMessage;
        // Not fatal: TD2 / DGTasks hours still load without a Classic id
      } finally {
        this.isLoadingTimeDoctorUser = false;
      }
    },
    async fetchTimeDoctorData() {
      if (!this.timeDoctorDateRange || this.timeDoctorDateRange.length < 2) {
        this.$toast.warning('Please select a date range first');
        return;
      }
      const sorted = [...this.timeDoctorDateRange].sort();
      const from = sorted[0];
      const to = sorted[1];
      this.isLoadingTimeDoctorData = true;
      this.timeDoctorError = null;
      try {
        console.log('Fetching Time Doctor data for period:', from, 'to', to, 'user_id:', this.timeDoctorUserId);
        const response = await this.$axios.get('/api/payroll/time-doctor/hours', {
          params: {
            from: from,
            to: to,
            company_id: this.timeDoctorCompanyId,
            user_id: this.timeDoctorUserId || undefined,
          },
        });
        console.log('Time Doctor API response:', response.data);
        if (response.data.response && response.data.data) {
          this.timeDoctorData = response.data.data;
          console.log('Parsed Time Doctor data:', this.timeDoctorData);
        } else {
          this.timeDoctorError = response.data.message || 'Failed to fetch Time Doctor data';
        }
      } catch (error) {
        console.error('Error fetching Time Doctor data:', error);
        this.timeDoctorError = error.response?.data?.message || error.message || 'Failed to fetch Time Doctor data';
      } finally {
        this.isLoadingTimeDoctorData = false;
      }
    },
    parseTimeDoctorHours(formattedTime) {
      if (!formattedTime) return null;
      const match = formattedTime.match(/(\d+)h\s*(\d+)m/);
      if (match) {
        return {
          hours: parseInt(match[1], 10),
          minutes: parseInt(match[2], 10),
        };
      }
      return null;
    },
    // Utility Methods
    getUserName(user) {
      if (!user) return "";
      if (user.name) {
        return user.name;
      }
      const firstName = user.first_name || "";
      const lastName = user.last_name || "";
      return `${firstName} ${lastName}`.trim() || "Unknown";
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      return this.$date(dateStr).format("MMM D, YYYY");
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "";
      return this.$date(dateStr).format("MMM D, YYYY h:mm A");
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined) return '-';
      return amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },
    getSubmissionStatusColor(status) {
      const colors = {
        pending: 'orange',
        approved: 'success',
        denied: 'error',
      };
      return colors[status] || 'grey';
    },
    // PT Form Methods
    submitPTForm() {
      this.isSubmitting = true;
      if (this.$refs.ptPayrollForm.validate()) {
        const sortedPeriod = [...this.ptFormData.invoicePeriod].sort();
        const payload = {
          email: this.ptFormData.email,
          name: this.ptFormData.name,
          clientName: this.ptFormData.clientName,
          invoicePeriod: {
            from: sortedPeriod[0] || null,
            to: sortedPeriod[1] || sortedPeriod[0] || null,
          },
          company: this.ptFormData.company
            ? {
              id: this.ptFormData.company.id,
              name: this.ptFormData.company.name,
            }
            : null,
          invoiceNumber: this.ptFormData.invoiceNumber,
          hoursWorked: {
            hours: this.ptFormData.hoursWorked.hours,
            minutes: this.ptFormData.hoursWorked.minutes,
          },
          currency: this.ptFormData.currency,
          invoiceAmount: this.ptFormData.invoiceAmount,
          // Pass actual file objects
          invoiceFile: this.ptFormData.invoiceFile,
          capacityUtilizationFile: this.ptFormData.capacityUtilizationFile,
          overtimeApprovalFile: this.ptFormData.overtimeApprovalFile,
          employeeDatabaseFormFile: this.ptFormData.employeeDatabaseFormFile,
          signatureAcknowledgementFile: this.ptFormData.signatureAcknowledgementFile,
          receiptFile: this.ptFormData.receiptFile,
          corFile: this.ptFormData.corFile,
          taxReturnFiles: this.ptFormData.taxReturnFiles,
          // Time Doctor data for validation
          timeDoctorHours: this.timeDoctorData ? this.timeDoctorHoursDecimal : null,
          comments: this.ptFormData.comments || null,
          submittedAt: new Date().toISOString(),
        };

        this.$emit("submitPT", payload);
      } else {
        this.isSubmitting = false;
      }
    },
    showPTSubmissionSuccess() {
      this.isSubmitting = false;
      this.ptSubmissionSuccess = true;
      this.resetPTForm();
    },
    enableSubmit() {
      this.isSubmitting = false;
    },
    startNewPTSubmission() {
      this.ptSubmissionSuccess = false;
    },
    resetPTForm() {
      this.ptFormData = {
        email: this.user?.email || "",
        name: this.getUserName(this.user || {}),
        clientName: "",
        invoicePeriod: [],
        company: null,
        invoiceNumber: "",
        currency: "USD",
        hoursWorked: {
          hours: null,
          minutes: null,
        },
        invoiceAmount: null,
        invoiceFile: null,
        capacityUtilizationFile: null,
        overtimeApprovalFile: null,
        employeeDatabaseFormFile: null,
        signatureAcknowledgementFile: null,
        receiptFile: null,
        comments: "",
      };
      this.$refs.ptPayrollForm?.resetValidation();
    },
    clearPTDateRange() {
      this.ptFormData.invoicePeriod = [];
    },
    viewPTSubmissionDetails(submission) {
      this.selectedSubmission = submission;
      this.detailsDialog = true;
    },
  },
};
</script>

<style scoped>
.submissions-wrapper {
  overflow-y: auto;
  max-height: 600px;
}

.submission-card {
  transition: all 0.2s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.submission-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.submission-card .v-card__text {
  overflow-y: auto;
  max-height: 300px;
  flex: 1;
}

.detail-label {
  font-size: 0.7rem;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
}

.detail-value {
  font-size: 0.85rem;
  font-weight: 500;
}
</style>
